import { datadogRum } from '@datadog/browser-rum';

// Loop guard lives in the URL, not storage: it must survive a hard reload, and
// in-memory/blocked-storage fallbacks don't (reload wipes them → infinite loop).
// Set before reloading; stripped on successful mount so a later failure retries.
const RELOAD_GUARD_PARAM = 'chunk_reload';

// Match the chunk-load error name Next's runtime uses (both webpack and Turbopack
// set error.name to this; same field Datadog reports as error.type) exactly to
// avoid false-positive reloads on unrelated errors. window.onerror only hands us
// a string, whose uncaught prefix carries the name — hence the message check.
export function isChunkLoadError(value: unknown): boolean {
  if (value === null || value === undefined) return false;

  const error = value instanceof Error ? value : undefined;
  if (error?.name === 'ChunkLoadError') return true;

  const message = error?.message ?? (typeof value === 'string' ? value : '');
  return message.includes('ChunkLoadError');
}

function hasReloadGuard(): boolean {
  return new URLSearchParams(window.location.search).has(RELOAD_GUARD_PARAM);
}

// Strip the guard param without reloading. Call on successful mount so a later
// transient failure in the same tab can retry, and the param doesn't linger in
// shared/bookmarked URLs.
export function clearChunkReloadGuard(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(RELOAD_GUARD_PARAM)) return;
  url.searchParams.delete(RELOAD_GUARD_PARAM);
  window.history.replaceState(window.history.state, '', url.toString());
}

// Hard-reload to fetch fresh HTML pointing at the current deployment's chunks.
// Fixes both failure modes on paid mobile roundups: (1) serving deployment aged
// past Vercel skew protection so `?dpl=<old>` chunks 404; (2) transient 4G drop.
// Reloads at most once (URL guard survives the reload) to avoid loops. Returns
// whether it reloaded; false means the caller should show an error UI instead.
export function attemptChunkReload(error: unknown): boolean {
  const hasAttemptedReload = hasReloadGuard();

  const message = hasAttemptedReload ? 'ChunkLoadError caught - reload already attempted' : 'ChunkLoadError caught - attempting recovery';

  datadogRum.addError(error instanceof Error ? error : new Error(String(error)), {
    message,
    chunkReloadAttempted: !hasAttemptedReload,
  });

  if (hasAttemptedReload) return false;

  const url = new URL(window.location.href);
  url.searchParams.set(RELOAD_GUARD_PARAM, '1');
  // replace() so the guard URL doesn't become a back-button entry.
  window.location.replace(url.toString());
  return true;
}
